import type { QuestionType } from "./types.js";

export function filterCsv(raw: string | null | undefined): string[] {
  return [
    ...new Set(
      (raw ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

/** Ordinary bookmarks tolerate stale values; lecture links are validated separately. */
export function practiceFilters(
  params: URLSearchParams,
  taxonomy: {
    chapters: readonly { number: number }[];
    topics: readonly { id: string; chapter: number }[];
  },
) {
  const ignoredFilters: string[] = [];
  function keep<T>(key: string, convert: (value: string) => T | undefined): T[] {
    const raw = filterCsv(params.get(key));
    const valid = raw.map(convert).filter((v): v is T => v !== undefined);
    if (params.has(key) && (!raw.length || valid.length !== raw.length)) ignoredFilters.push(key);
    return valid;
  }
  const chapters = keep("chapters", (value) => {
    const n = Number(value);
    return Number.isInteger(n) && taxonomy.chapters.some((ch) => ch.number === n) ? n : undefined;
  });
  const validTopics = keep("topics", (value) =>
    taxonomy.topics.some((t) => t.id === value) ? value : undefined,
  );
  const mode =
    params.get("mode") === "mixed"
      ? "mixed"
      : params.get("mode") === "topic" || chapters.length > 0 || validTopics.length > 0
        ? "topic"
        : "mixed";
  const topics = mode === "mixed" ? [] : validTopics;
  if (mode === "mixed" && params.has("topics") && !ignoredFilters.includes("topics"))
    ignoredFilters.push("topics");
  // Explicit mixed mode deliberately ignores topics; stale topic-only bookmarks must not widen.
  const invalidTopics =
    params.get("mode") !== "mixed" &&
    filterCsv(params.get("topics")).length > 0 &&
    validTopics.length === 0 &&
    chapters.length === 0;
  const types = keep<QuestionType>("types", (value) =>
    value === "mc" || value === "numeric" || value === "open" ? value : undefined,
  );
  return { mode, chapters, topics, types, ignoredFilters, invalidTopics } as const;
}
